import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Saved views bar (§7.1) for the Tickets list.
 *
 * Shows the user's personal + the tenant's shared views as one-click chips,
 * and a "Save view" affordance that captures the CURRENT filter querystring.
 * Applying a view just navigates with its querystring — the list already
 * reads every filter from the URL, so nothing else changes. Renders nothing
 * for users without `saved_view.read`.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bookmark, Plus, Users, X } from 'lucide-react';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { Field, Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { api, ApiError } from '@/lib/api';
import { hasPerm, useAuthStore } from '@/store/auth';
import { t } from '@/lib/i18n';
export function SavedViewsBar() {
    const me = useAuthStore((s) => s.me);
    const qc = useQueryClient();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [saveOpen, setSaveOpen] = useState(false);
    const canUse = hasPerm(me, 'saved_view.read');
    const viewsQ = useQuery({
        queryKey: ['saved-views', 'ticket'],
        queryFn: () => api.get('/saved-views?entity_type=ticket'),
        enabled: canUse,
    });
    const del = useMutation({
        mutationFn: (id) => api.del(`/saved-views/${id}`),
        onSuccess: () => qc.invalidateQueries({ queryKey: ['saved-views', 'ticket'] }),
    });
    if (!canUse)
        return null;
    const views = viewsQ.data ?? [];
    const currentQs = searchParams.toString();
    return (_jsxs("div", { className: "mb-3 flex flex-wrap items-center gap-2", children: [_jsxs("span", { className: "flex items-center gap-1 text-xs font-semibold text-slate-500 dark:text-slate-400", children: [_jsx(Bookmark, { className: "h-3.5 w-3.5" }), " Views"] }), views.map((v) => {
                const active = v.query_string === currentQs;
                const mine = v.owner_user_id === me?.user.id;
                return (_jsxs("span", { className: [
                        'group inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs',
                        active
                            ? 'border-brand-500 bg-brand-50 text-brand-700 dark:bg-brand-950/40 dark:text-brand-300'
                            : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300',
                    ].join(' '), children: [_jsxs("button", { type: "button", onClick: () => navigate(`/tickets${v.query_string ? `?${v.query_string}` : ''}`), children: [v.is_shared && _jsx(Users, { className: "mr-1 inline h-3 w-3 text-slate-400" }), v.name] }), (mine || (v.is_shared && hasPerm(me, 'saved_view.manage_shared'))) && (_jsx("button", { type: "button", "aria-label": `Delete view ${v.name}`, onClick: () => del.mutate(v.id), className: "opacity-0 transition group-hover:opacity-100", children: _jsx(X, { className: "h-3 w-3 text-slate-400 hover:text-rose-500" }) }))] }, v.id));
            }), _jsxs("button", { type: "button", onClick: () => setSaveOpen(true), className: "inline-flex items-center gap-1 rounded-full border border-dashed border-slate-300 px-2.5 py-0.5 text-xs text-slate-500 hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-800", children: [_jsx(Plus, { className: "h-3 w-3" }), " Save current view"] }), saveOpen && (_jsx(SaveViewModal, { currentQs: currentQs, canShare: hasPerm(me, 'saved_view.manage_shared'), onClose: () => setSaveOpen(false), onSaved: () => {
                    qc.invalidateQueries({ queryKey: ['saved-views', 'ticket'] });
                    setSaveOpen(false);
                } }))] }));
}
function SaveViewModal({ currentQs, canShare, onClose, onSaved, }) {
    const [name, setName] = useState('');
    const [shared, setShared] = useState(false);
    const [error, setError] = useState(null);
    const m = useMutation({
        mutationFn: () => api.post('/saved-views', {
            name: name.trim(),
            query_string: currentQs,
            entity_type: 'ticket',
            is_shared: shared,
        }),
        onSuccess: onSaved,
        onError: (e) => setError(e instanceof ApiError ? e.message : 'Save failed.'),
    });
    return (_jsx(Modal, { open: true, onClose: onClose, title: "Save current view", size: "sm", children: _jsxs("div", { className: "space-y-4", children: [_jsx(Field, { label: t('View name'), required: true, children: _jsx(Input, { value: name, onChange: (e) => setName(e.target.value), placeholder: t('e.g. My open P1s'), autoFocus: true }) }), _jsxs("p", { className: "text-xs text-slate-500", children: ["Captures the current filters and sort: ", _jsx("code", { className: "break-all", children: currentQs || '(no filters)' })] }), canShare && (_jsxs("label", { className: "flex items-center gap-2 text-sm", children: [_jsx("input", { type: "checkbox", checked: shared, onChange: (e) => setShared(e.target.checked) }), "Share with the whole team"] })), error && _jsx("div", { className: "rounded bg-rose-50 p-2 text-sm text-rose-700 dark:bg-rose-950/40 dark:text-rose-300", children: error }), _jsxs("div", { className: "flex justify-end gap-2", children: [_jsx(Button, { variant: "ghost", onClick: onClose, children: "Cancel" }), _jsx(Button, { onClick: () => m.mutate(), disabled: m.isPending || !name.trim(), children: "Save view" })] })] }) }));
}
